import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { CopyButton } from '../../../components/CopyButton';
import { Section } from '../../../components/Section';
import { Text } from '../../../components/Text';
import { atiraLinkTestIDs } from '../../../constants/test/atiralink.testIds';
import { CreateAtiraLinkDto } from '../../../model/atira-link/dto/CreateAtiraLinkDto';
import { AtiraLinkPlanType } from '../../../model/plan/types/AtiraLinkPlanType.enum';
import { Lengths } from '../../../model/shared/enum/Lengths.enum';
import { atiraLinkSliceSelectors } from '../../../redux/atira-link/atira-link.selector';
import { useAppSelector } from '../../../redux/store';
import { userSliceSelectors } from '../../../redux/user/user.selector';
import { FontSize } from '../../../theme/FontSize';
import { Spacing } from '../../../theme/Spacing';
import { LinkInput } from './LinkInput';

const StyledButton = styled(CopyButton)`
  font-size: ${FontSize.s};
  padding: ${Spacing.s};
  margin: 0rem;
  color: ${({ theme }) => theme.fgMuted};
`;

const LinkText = styled(Text)`
  color: ${({ theme }) => theme.fgMuted};
  display: flex;
  align-items: center;
`;

const SpanWithNoWrap = styled.span`
  white-space: nowrap;
`;

const LinkSpan = styled(SpanWithNoWrap)`
  direction: ltr;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const AtiraLinkContentLink: React.FC = () => {
  const { t } = useTranslation();

  const { control, clearErrors } = useFormContext<CreateAtiraLinkDto>();

  const atiralink = useAppSelector(
    atiraLinkSliceSelectors.selectSelectedAtiraLink,
  );
  const atiraLinkCreateEdit = useAppSelector(
    atiraLinkSliceSelectors.selectSelectedAtiraLink,
  );
  const user = useAppSelector(userSliceSelectors.selectLoggedInUser)!;

  const editMode = Boolean(atiralink?._id);
  const userPlan =
    user.plan?.freeTrial?.freeTrialPlan ||
    user.plan?.unpaidPlan?.unpaidPlanType ||
    user.plan?.type;
  const canEditLinkName = userPlan === AtiraLinkPlanType.PLAN_2;
  const isLinkNameInputDisabled = canEditLinkName ? false : editMode;

  const linkName = editMode
    ? atiralink?.linkName
    : atiraLinkCreateEdit?.linkName;

  const userLinkUrl = `https://atrl.ink/${linkName}`;

  return (
    <Section title={t('common.link')}>
      <LinkText>
        <SpanWithNoWrap>
          {t(
            `atira_link.create.content.link_name.desdcription${editMode ? '.edit' : ''}`,
          )}
        </SpanWithNoWrap>
        <LinkSpan>
          <strong> https://atrl.ink/</strong>
          <span style={{ fontWeight: 'bold', textDecoration: 'underline' }}>
            {linkName}
          </span>
        </LinkSpan>
        <StyledButton copy={userLinkUrl} />
      </LinkText>

      <Controller
        control={control}
        name="linkName"
        rules={{
          required: true,
          minLength: {
            value: Lengths.MIN_3,
            message: t('error.range', {
              name: t('atira_link.create.content.link_name'),
              min: Lengths.MIN_3,
              max: Lengths.NAME,
            }),
          },
          maxLength: {
            value: Lengths.NAME,
            message: t('error.range', {
              name: t('atira_link.create.content.link_name'),
              min: Lengths.MIN_3,
              max: Lengths.NAME,
            }),
          },
        }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <LinkInput
            title={`${t('atira_link.create.content.link_name')}:`}
            required
            value={value}
            onChange={(e) => onChange(e.target.value.toLowerCase().trim())}
            minLength={Lengths.MIN_3}
            maxLength={Lengths.NAME}
            errorMessage={error?.message}
            valid={!error}
            disabled={isLinkNameInputDisabled}
            onFocus={() => clearErrors('linkName')}
            placeholder={t('atira_link.create.content.link_name.length.note')}
            dataTestId={atiraLinkTestIDs.content.linkName}
          />
        )}
      />
    </Section>
  );
};
